import React, { memo, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type { ChartBlock } from '../../../types/backend';
import BlockShell from './BlockShell';
import { chartPalette, makeValueFormatter } from './blockFormat';

interface Props {
  block: ChartBlock;
}

const CHART_HEIGHT = 280;

function describe(block: ChartBlock): string {
  const fmt = makeValueFormatter(block.value_format);
  const axis = block.x_axis.length ? `Categories: ${block.x_axis.join(', ')}. ` : '';
  const series = block.series
    .map((s) => `${s.label}: ${s.data.map((d) => fmt(d)).join(', ')}`)
    .join('. ');
  return `${block.chart_type} chart. ${axis}${series}.`;
}

const ChartBlockView: React.FC<Props> = memo(({ block }) => {
  const theme = useTheme();
  const colors = chartPalette(theme);
  const formatter = makeValueFormatter(block.value_format);
  const summary = useMemo(() => describe(block), [block]);

  const axisSx = {
    '& .MuiChartsAxis-tickLabel': { fill: theme.palette.text.secondary, fontSize: '0.75rem' },
    '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
    '& .MuiChartsAxis-tick': { stroke: theme.palette.divider },
  };

  let chart: React.ReactNode;

  if (block.chart_type === 'pie') {
    const first = block.series[0];
    const data = (first?.data ?? []).map((value, i) => ({
      id: i,
      value,
      label: block.x_axis[i] ?? first?.label ?? `#${i + 1}`,
      color: colors[i % colors.length],
    }));
    chart = (
      <PieChart
        colors={colors}
        series={[{ data, innerRadius: 30, paddingAngle: 1, cornerRadius: 3, valueFormatter: (item) => formatter(item.value) }]}
        height={CHART_HEIGHT}
        hideLegend={false}
      />
    );
  } else if (block.chart_type === 'bar') {
    chart = (
      <BarChart
        colors={colors}
        xAxis={[{ data: block.x_axis, scaleType: 'band' }]}
        yAxis={[{ valueFormatter: formatter }]}
        series={block.series.map((s) => ({ data: s.data, label: s.label, valueFormatter: formatter }))}
        height={CHART_HEIGHT}
        sx={axisSx}
      />
    );
  } else {
    // line and area share LineChart; area fills under the curve.
    const isArea = block.chart_type === 'area';
    chart = (
      <LineChart
        colors={colors}
        xAxis={[{ data: block.x_axis, scaleType: 'point' }]}
        yAxis={[{ valueFormatter: formatter }]}
        series={block.series.map((s) => ({
          data: s.data,
          label: s.label,
          area: isArea,
          showMark: block.series.length === 1,
          valueFormatter: formatter,
        }))}
        height={CHART_HEIGHT}
        sx={axisSx}
      />
    );
  }

  return (
    <BlockShell bodyPadding={0.5}>
      <Box role="img" aria-label={summary}>
        {chart}
        <Box component="span" sx={visuallyHidden}>
          {summary}
        </Box>
      </Box>
      {block.caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 1.5, pb: 1, textAlign: 'center' }}
        >
          {block.caption}
        </Typography>
      )}
    </BlockShell>
  );
});

ChartBlockView.displayName = 'ChartBlockView';
export default ChartBlockView;
